import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useUserStore } from '../stores/useUserStore';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Save,
  DollarSign,
  Calendar,
  Tag,
  CreditCard,
  FileText,
  Trash2,
  Check,
  ChevronsUpDown,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { nekoPos } from 'public-contracts';
import CostService from '../services/costService';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import { cn } from '../utils';

interface CostFormData {
  description: string;
  amount: number;
  categoryId: string;
  method: string;
  date: string;
  notes: string;
}

type Cost = nekoPos.Cost;
type CostCategory = nekoPos.CostCategory;

const ModernCostEdit: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { user, fetchUser } = useUserStore();
  const { id } = useParams<{ id: string }>();
  const costService = useMemo(() => new CostService(), []);

  const [cost, setCost] = useState<Cost | null>(null);
  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');

  const methods = useForm<CostFormData>({
    defaultValues: {
      description: '',
      amount: 0,
      categoryId: '',
      method: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
    },
  });

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const selectedCategoryId = watch('categoryId');
  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);

  // Clear search when popover opens
  useEffect(() => {
    if (categoryOpen) {
      setCategorySearch('');
    }
  }, [categoryOpen]);

  // Load real data
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const [costData, categoriesData] = await Promise.all([
          costService.getCost(id),
          costService.getCostCategories(),
        ]);

        setCost(costData);
        setCategories(categoriesData);

        // Set form values
        setValue('description', costData.description);
        setValue('amount', costData.amount);
        setValue('categoryId', costData.categoryId);
        setValue('method', costData.method || '');
        setValue('date', costData.date.toISOString().split('T')[0]);
        setValue('notes', costData.notes || '');
      } catch (error) {
        console.error('Error loading cost data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, setValue, costService]);

  const hasPermission = (action: 'create' | 'edit' | 'delete') => {
    if (!user?.roles) return false;
    if (user.roles.includes('sysAdmin')) return true;
    if (user.roles.includes('admin')) return true;
    if (user.roles.includes('staff')) return true;
    if (user.roles.includes('storeManager')) return true;
    if (user.roles.includes('cashier')) {
      return action !== 'delete';
    }
    return false;
  };

  if (!hasPermission('edit')) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted">
        <div className="text-center">
          <p className="text-red-600 mb-4">{translate('common.accessDenied', 'Access Denied')}</p>
          <p className="text-muted-foreground">
            {translate('pos.costs.no_permission_edit')}
          </p>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: CostFormData) => {
    if (!id) return;

    setSaving(true);
    try {
      const costData: Partial<Cost> = {
        description: data.description,
        amount: data.amount,
        categoryId: data.categoryId,
        method: data.method || undefined,
        date: new Date(data.date),
        notes: data.notes || undefined,
      };

      await costService.updateCost(id, costData);

      // Redirect to cost list
      navigate('/costs');
    } catch (error) {
      console.error('Error updating cost:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !hasPermission('delete')) return;

    if (
      confirm(
        'Are you sure you want to delete this cost entry? This action cannot be undone.'
      )
    ) {
      setDeleting(true);
      try {
        await costService.deleteCost(id);

        // Redirect to cost list
        navigate('/costs');
      } catch (error) {
        console.error('Error deleting cost:', error);
      } finally {
        setDeleting(false);
      }
    }
  };

  const handleCancel = () => {
    navigate('/costs');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2 mx-auto"></div>
          <span className="text-muted-foreground">
            {translate('pos.common.loadingCostEntry')}
          </span>
        </div>
      </div>
    );
  }

  if (!cost) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted">
        <div className="text-center">
          <p className="text-red-600 mb-4">Cost not found</p>
          <Button onClick={handleCancel}>Back to Costs</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            className="border-border text-foreground hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Edit Cost Entry
            </h1>
            <p className="text-muted-foreground">Update expense details</p>
          </div>
        </div>
        {hasPermission('delete') && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            disabled={deleting}
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            {deleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </>
            )}
          </Button>
        )}
      </div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-3 space-y-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Basic Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label
                      htmlFor="description"
                      className="text-sm font-medium"
                    >
                      Description *
                    </Label>
                    <Input
                      id="description"
                      placeholder="Enter cost description..."
                      {...register('description', {
                        required: 'Description is required',
                      })}
                      className="mt-1"
                    />
                    {errors.description && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.description.message}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="amount" className="text-sm font-medium">
                        Amount *
                      </Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="amount"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          {...register('amount', {
                            required: 'Amount is required',
                            min: {
                              value: 0,
                              message: 'Amount must be positive',
                            },
                          })}
                          className="pl-10"
                        />
                      </div>
                      {errors.amount && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.amount.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="date" className="text-sm font-medium">
                        Date *
                      </Label>
                      <div className="relative mt-1">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="date"
                          type="date"
                          {...register('date', {
                            required: 'Date is required',
                          })}
                          className="pl-10"
                        />
                      </div>
                      {errors.date && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.date.message}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Category and Method */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="h-5 w-5" />
                    Category & Payment
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label
                        htmlFor="categoryId"
                        className="text-sm font-medium"
                      >
                        Category *
                      </Label>
                      <Popover
                        open={categoryOpen}
                        onOpenChange={setCategoryOpen}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={categoryOpen}
                            className={cn(
                              'w-full justify-between mt-1 pl-10',
                              !selectedCategoryId && 'text-muted-foreground'
                            )}
                          >
                            <div className="flex items-center">
                              <Tag className="absolute left-3 h-4 w-4 text-muted-foreground" />
                              <span className="ml-2">
                                {selectedCategory
                                  ? selectedCategory.name
                                  : 'Select a category'}
                              </span>
                            </div>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput
                              placeholder="Search categories..."
                              value={categorySearch}
                              onValueChange={setCategorySearch}
                            />
                            <CommandList>
                              <CommandEmpty>No category found.</CommandEmpty>
                              <CommandGroup>
                                {Array.isArray(categories) &&
                                  categories.map((category) => (
                                    <CommandItem
                                      key={category.id}
                                      value={`${category.name} ${
                                        category.description || ''
                                      }`}
                                      onSelect={() => {
                                        setValue('categoryId', category.id, {
                                          shouldValidate: true,
                                        });
                                        setCategoryOpen(false);
                                        setCategorySearch('');
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          'mr-2 h-4 w-4',
                                          selectedCategoryId === category.id
                                            ? 'opacity-100'
                                            : 'opacity-0'
                                        )}
                                      />
                                      {category.name}
                                    </CommandItem>
                                  ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {errors.categoryId && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.categoryId.message}
                        </p>
                      )}
                      <input
                        type="hidden"
                        {...register('categoryId', {
                          required: 'Category is required',
                        })}
                      />
                    </div>

                    <div>
                      <Label htmlFor="method" className="text-sm font-medium">
                        Payment Method
                      </Label>
                      <div className="relative mt-1">
                        <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="method"
                          placeholder="e.g., Cash, Bank Transfer, Credit Card"
                          {...register('method')}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Additional Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label htmlFor="notes" className="text-sm font-medium">
                      Notes
                    </Label>
                    <Textarea
                      id="notes"
                      placeholder="Add any additional notes or details..."
                      {...register('notes')}
                      className="mt-1"
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-4 space-y-4">
                {/* Summary Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount:</span>
                      <span className="font-medium">
                        ${watch('amount') || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Category:</span>
                      <span className="font-medium">
                        {(Array.isArray(categories) &&
                          categories.find((c) => c.id === watch('categoryId'))
                            ?.name) ||
                          'Not selected'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date:</span>
                      <span className="font-medium">
                        {watch('date') || 'Not selected'}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Actions */}
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <Button type="submit" className="w-full" disabled={saving}>
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Update Cost Entry
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      className="w-full border-border text-foreground hover:bg-muted"
                    >
                      Cancel
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default ModernCostEdit;
